import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import styles from './Dashboard.module.css';

// svg icons
const ico = (d, extra = {}) => {
    const p = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', style: { flexShrink: 0 }, ...extra };
    return <svg {...p}>{d}</svg>;
};

// Button bar icons
const IconClose     = () => ico(<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>);
const IconAppstore  = () => ico(<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>);
const IconManage    = () => ico(<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><line x1="10" y1="17.5" x2="21" y2="17.5"/><line x1="17.5" y1="10" x2="17.5" y2="21"/></>);
const IconCaret     = ({ open }) => ico(<polyline points={open ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}/>, { width: 10, height: 10 });
const IconChevron   = ({ open }) => ico(<polyline points={open ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}/>, { width: 12, height: 12 });

// Widget-list icons (matching accounts renderIcon switch)
const renderIcon = (type) => {
    const p = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', style: { flexShrink: 0 } };
    switch (type) {
        case 'chart':    return <svg {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
        case 'pie':      return <svg {...p}><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>;
        case 'table':    return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg>;
        case 'list':     return <svg {...p}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
        case 'stats':    return <svg {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
        case 'activity': return <svg {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
        default:         return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
    }
};

const STAT_SIZE = { minW: 2, minH: 2, maxW: 6, maxH: 4, w: 3, h: 2 };

const WIDGET_CATALOGUE = [
    // ── individual stat counters (Overview) ───────────────────────────────────
    // Row 1: open, active, need_confirmation, need_clarification
    { code: 'stat_open',                name: 'Open',                category: 'Stats', type: 'stats', size: STAT_SIZE },
    { code: 'stat_active',              name: 'Active',              category: 'Stats', type: 'stats', size: STAT_SIZE },
    { code: 'stat_need_confirmation',   name: 'Need Confirmation',   category: 'Stats', type: 'stats', size: STAT_SIZE },
    { code: 'stat_need_clarification',  name: 'Need Clarification',  category: 'Stats', type: 'stats', size: STAT_SIZE },
    // Row 2: total, pending, overdue, in_progress
    { code: 'stat_total',               name: 'Total Tickets',       category: 'Stats', type: 'stats', size: STAT_SIZE },
    { code: 'stat_pending',             name: 'Pending',             category: 'Stats', type: 'stats', size: STAT_SIZE },
    { code: 'stat_overdue',             name: 'Overdue',             category: 'Stats', type: 'stats', size: STAT_SIZE },
    { code: 'stat_in_progress',         name: 'In Progress',         category: 'Stats', type: 'stats', size: STAT_SIZE },
    // Row 3: closed
    { code: 'stat_closed',              name: 'Closed',              category: 'Stats', type: 'stats', size: STAT_SIZE },
    // ── charts ────────────────────────────────────────────────────────────────
    { code: 'tickets_created',          name: 'Tickets Created',          category: 'Charts',   type: 'chart',    subtitle: 'Daily volume of new tickets (last 30 days)',   size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 7, h: 6 } },
    { code: 'tickets_by_status',        name: 'Tickets by Status',        category: 'Charts',   type: 'chart',                                                                      size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 5, h: 6 } },
    { code: 'tickets_by_priority',      name: 'Tickets by Priority',      category: 'Charts',   type: 'pie',                                                                        size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 4, h: 6 } },
    { code: 'tickets_by_department',    name: 'Tickets per Department',   category: 'Charts',   type: 'chart',                                                                      size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 8, h: 6 } },
    { code: 'resolved_tickets',         name: 'Resolved Tickets',         category: 'Charts',   type: 'chart',    subtitle: 'Daily count of closed tickets (last 30 days)', size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 6, h: 6 } },
    { code: 'avg_response_time',        name: 'Avg Response Time',        category: 'Charts',   type: 'chart',    subtitle: 'Minutes from creation to first reply (last 30 days)',         size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 6, h: 6 } },
    { code: 'claritask_comparison',     name: 'Claritask Comparison',     category: 'Charts',   type: 'pie',      subtitle: 'Single vs linked tickets',                     size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 6, h: 6 } },
    // ── team ─────────────────────────────────────────────────────────────────
    { code: 'tickets_per_agent',        name: 'Tickets per Agent',        category: 'Team',     type: 'chart',                                                                            size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 6, h: 6 } },
    { code: 'top_agents',               name: 'Top Agents',               category: 'Team',     type: 'list',     subtitle: 'Most resolved tickets — all time',             size: { minW: 4, minH: 5, maxW: 12, maxH: 11,  w: 6, h: 6 } },
    // ── activity ─────────────────────────────────────────────────────────────
    { code: 'recent_activity',          name: 'Recent Activity',          category: 'Activity', type: 'activity',                                                                   size: { minW: 4, minH: 5, maxW: 12, maxH: 14, w: 6, h: 7 } },
    { code: 'closed_tickets',           name: 'Recently Resolved Tickets',category: 'Activity', type: 'list',                                                                       size: { minW: 4, minH: 5, maxW: 12, maxH: 8,  w: 12, h: 5 } },
    { code: 'last_updated_tickets',     name: 'Recently Updated Tickets', category: 'Activity', type: 'table',                                                                      size: { minW: 5, minH: 7, maxW: 12, maxH: 14, w: 12, h: 9 } },
];

const STORAGE_KEY = 'claritick_dashboard_layout';

// indices in WIDGET_CATALOGUE:
// Row 1 stats: 0=open, 1=active, 2=need_confirmation, 3=need_clarification
// Row 2 stats: 4=total, 5=pending, 6=overdue, 7=in_progress
// Row 3 stats: 8=closed
// 9=tickets_created, 10=tickets_by_status,
// 11=tickets_by_priority, 12=tickets_by_department, 13=resolved_tickets,
// 14=avg_response_time, 15=claritask_comparison,
// 16=tickets_per_agent, 17=top_agents,
// 18=recent_activity, 19=closed_tickets, 20=last_updated_tickets

// stat grid: row1 & row2 = 4 cards × w:3; row3 = 1 card (closed) full-width w:12
const S = { minW: 2, minH: 2, maxW: 6, maxH: 4 };
const C = { minW: 4, minH: 5, maxW: 12, maxH: 11 };

// Widget order for DEFAULT_LAYOUT:
// layout-0  → top_agents          (Team,    idx 17)
// layout-1  → stat_open           (Stats,   idx 0)
// layout-2  → stat_active         (Stats,   idx 1)
// layout-3  → stat_in_progress    (Stats,   idx 7)
// layout-4  → stat_overdue        (Stats,   idx 6)
// layout-5  → stat_pending        (Stats,   idx 5)
// layout-6  → stat_total          (Stats,   idx 4)
// layout-7  → stat_need_clarification  (Stats, idx 3)
// layout-8  → stat_need_confirmation  (Stats, idx 2)
// layout-9  → stat_closed         (Stats,   idx 8)
// layout-10 → tickets_created     (Charts,  idx 9)
// layout-11 → tickets_by_status   (Charts,  idx 10)
// layout-12 → tickets_by_priority (Charts,  idx 11)
// layout-13 → tickets_by_department (Charts, idx 12)
// layout-14 → resolved_tickets    (Charts,  idx 13)
const DEFAULT_LAYOUT = {
    widgets: [
        { ...WIDGET_CATALOGUE[17], layout_id: 'layout-0'  }, // top_agents
        { ...WIDGET_CATALOGUE[0],  layout_id: 'layout-1'  }, // stat_open
        { ...WIDGET_CATALOGUE[1],  layout_id: 'layout-2'  }, // stat_active
        { ...WIDGET_CATALOGUE[7],  layout_id: 'layout-3'  }, // stat_in_progress
        { ...WIDGET_CATALOGUE[6],  layout_id: 'layout-4'  }, // stat_overdue
        { ...WIDGET_CATALOGUE[5],  layout_id: 'layout-5'  }, // stat_pending
        { ...WIDGET_CATALOGUE[4],  layout_id: 'layout-6'  }, // stat_total
        { ...WIDGET_CATALOGUE[3],  layout_id: 'layout-7'  }, // stat_need_clarification
        { ...WIDGET_CATALOGUE[2],  layout_id: 'layout-8'  }, // stat_need_confirmation
        { ...WIDGET_CATALOGUE[8],  layout_id: 'layout-9'  }, // stat_closed
        { ...WIDGET_CATALOGUE[9],  layout_id: 'layout-10' }, // tickets_created
        { ...WIDGET_CATALOGUE[10], layout_id: 'layout-11' }, // tickets_by_status
        { ...WIDGET_CATALOGUE[11], layout_id: 'layout-12' }, // tickets_by_priority
        { ...WIDGET_CATALOGUE[12], layout_id: 'layout-13' }, // tickets_by_department
        { ...WIDGET_CATALOGUE[13], layout_id: 'layout-14' }, // resolved_tickets
    ],
    layouts: {
        lg: [
            // ── Row 1: Top Agents (left half) | 3 stat cards (right half, row 1) ──
            { i: 'layout-0', x: 0, y: 0, w: 6, h: 6, ...C },   // top_agents
            { i: 'layout-1', x: 6, y: 0, w: 2, h: 2, ...S },   // open
            { i: 'layout-2', x: 8, y: 0, w: 2, h: 2, ...S },   // active
            { i: 'layout-3', x: 10, y: 0, w: 2, h: 2, ...S },  // in_progress
            // ── right half row 2: overdue | pending | total ──
            { i: 'layout-4', x: 6, y: 2, w: 2, h: 2, ...S },   // overdue
            { i: 'layout-5', x: 8, y: 2, w: 2, h: 2, ...S },   // pending
            { i: 'layout-6', x: 10, y: 2, w: 2, h: 2, ...S },  // total
            // ── right half row 3: need_clarification | need_confirmation | closed ──
            { i: 'layout-7', x: 6, y: 4, w: 2, h: 2, ...S },   // need_clarification
            { i: 'layout-8', x: 8, y: 4, w: 2, h: 2, ...S },   // need_confirmation
            { i: 'layout-9', x: 10, y: 4, w: 2, h: 2, ...S },  // closed
            // ── Row 2: Tickets Created | Tickets by Status ──
            { i: 'layout-10', x: 0, y: 6,  w: 7, h: 6, ...C },
            { i: 'layout-11', x: 7, y: 6,  w: 5, h: 6, ...C },
            // ── Row 3: Tickets by Priority | Tickets per Department ──
            { i: 'layout-12', x: 0, y: 12, w: 4, h: 6, ...C },
            { i: 'layout-13', x: 4, y: 12, w: 8, h: 6, ...C },
            // ── Row 4: Resolved Tickets (full width) ──
            { i: 'layout-14', x: 0, y: 18, w: 12, h: 6, ...C },
        ],
        md: [
            // ── Top Agents full width ──
            { i: 'layout-0', x: 0, y: 0, w: 6, h: 6, minW: 4, minH: 5, maxW: 6, maxH: 11 },
            // ── Stats 3-in-a-row ──
            { i: 'layout-1', x: 0, y: 6,  w: 2, h: 2, ...S },
            { i: 'layout-2', x: 2, y: 6,  w: 2, h: 2, ...S },
            { i: 'layout-3', x: 4, y: 6,  w: 2, h: 2, ...S },
            { i: 'layout-4', x: 0, y: 8,  w: 2, h: 2, ...S },
            { i: 'layout-5', x: 2, y: 8,  w: 2, h: 2, ...S },
            { i: 'layout-6', x: 4, y: 8,  w: 2, h: 2, ...S },
            { i: 'layout-7', x: 0, y: 10, w: 2, h: 2, ...S },
            { i: 'layout-8', x: 2, y: 10, w: 2, h: 2, ...S },
            { i: 'layout-9', x: 4, y: 10, w: 2, h: 2, ...S },
            // ── Charts ──
            { i: 'layout-10', x: 0, y: 12, w: 4, h: 6, minW: 4, minH: 5, maxW: 6, maxH: 11 },
            { i: 'layout-11', x: 4, y: 12, w: 2, h: 6, minW: 4, minH: 5, maxW: 6, maxH: 11 },
            { i: 'layout-12', x: 0, y: 18, w: 2, h: 6, minW: 4, minH: 5, maxW: 6, maxH: 11 },
            { i: 'layout-13', x: 2, y: 18, w: 4, h: 6, minW: 4, minH: 5, maxW: 6, maxH: 11 },
            { i: 'layout-14', x: 0, y: 24, w: 6, h: 6, minW: 4, minH: 5, maxW: 6, maxH: 11 },
        ],
        xs: [
            { i: 'layout-0',  x: 0, y: 0,  w: 2, h: 6, minW: 2, minH: 5, maxW: 2, maxH: 11 },
            { i: 'layout-1',  x: 0, y: 6,  w: 2, h: 2, ...S },
            { i: 'layout-2',  x: 0, y: 8,  w: 2, h: 2, ...S },
            { i: 'layout-3',  x: 0, y: 10, w: 2, h: 2, ...S },
            { i: 'layout-4',  x: 0, y: 12, w: 2, h: 2, ...S },
            { i: 'layout-5',  x: 0, y: 14, w: 2, h: 2, ...S },
            { i: 'layout-6',  x: 0, y: 16, w: 2, h: 2, ...S },
            { i: 'layout-7',  x: 0, y: 18, w: 2, h: 2, ...S },
            { i: 'layout-8',  x: 0, y: 20, w: 2, h: 2, ...S },
            { i: 'layout-9',  x: 0, y: 22, w: 2, h: 2, ...S },
            { i: 'layout-10', x: 0, y: 24, w: 2, h: 6, minW: 2, minH: 5, maxW: 2, maxH: 11 },
            { i: 'layout-11', x: 0, y: 30, w: 2, h: 6, minW: 2, minH: 5, maxW: 2, maxH: 11 },
            { i: 'layout-12', x: 0, y: 36, w: 2, h: 6, minW: 2, minH: 5, maxW: 2, maxH: 11 },
            { i: 'layout-13', x: 0, y: 42, w: 2, h: 6, minW: 2, minH: 5, maxW: 2, maxH: 11 },
            { i: 'layout-14', x: 0, y: 48, w: 2, h: 6, minW: 2, minH: 5, maxW: 2, maxH: 11 },
        ],
    },
};

// ── Manage component ──────────────────────────────────────────────────────────
const Manage = ({ onDragStart, onAddWidget, onEditable, onLayoutChange, onWidgetsChange, onSave, currentWidgets, currentLayouts, snapshot, edit, onEditChange, onEnterEdit, isLoading }) => {
    const [search, setSearch] = useState('');
    const [panelOpen, setPanelOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // all categories open by default
    const [openCategories, setOpenCategories] = useState(() =>
        [...new Set(WIDGET_CATALOGUE.map(w => w.category))]
    );
    const panelRef = useRef(null);
    const panelDropRef = useRef(null);
    const draggingRef = useRef(false);

    const categories = [...new Set(WIDGET_CATALOGUE.map(w => w.category))];
    const filtered = WIDGET_CATALOGUE.filter(w =>
        w.name.toLowerCase().includes(search.toLowerCase()) ||
        w.category.toLowerCase().includes(search.toLowerCase())
    );

    // when searching, expand all categories that have results
    useEffect(() => {
        if (search) {
            const activeCats = [...new Set(filtered.map(w => w.category))];
            setOpenCategories(activeCats);
        }
    }, [search]);

    const toggleCategory = (cat) => {
        setOpenCategories(prev =>
            prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
        );
    };

    // close panel when clicking outside — but NOT during a drag
    useEffect(() => {
        if (!panelOpen) return;
        const handler = (e) => {
            if (draggingRef.current) return;
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setPanelOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [panelOpen]);

    // flip panel to the right if it would overflow off the left edge of the viewport
    useEffect(() => {
        if (!panelOpen || !panelDropRef.current) return;
        const rect = panelDropRef.current.getBoundingClientRect();
        if (rect.left < 0) {
            panelDropRef.current.style.right = 'auto';
            panelDropRef.current.style.left  = '0';
        } else {
            panelDropRef.current.style.right = '0';
            panelDropRef.current.style.left  = 'auto';
        }
    }, [panelOpen]);

    const isMobile = () => ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

    const handleWidgetTap = (widget) => {
        if (!isMobile()) return;          // desktop uses drag-and-drop
        onAddWidget(widget);
        setPanelOpen(false);
    };

    const handleCancel = () => {
        // Restore from localStorage (last saved state), falling back to snapshot
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.widgets && parsed.layouts) {
                    onWidgetsChange(parsed.widgets);
                    onLayoutChange(parsed.layouts);
                    onEditChange(false);
                    onEditable(false);
                    setPanelOpen(false);
                    return;
                }
            }
        } catch (e) {
            console.error('Failed to read layout from localStorage:', e);
        }
        // Fallback to in-memory snapshot
        if (snapshot) {
            onWidgetsChange(snapshot.widgets);
            onLayoutChange(snapshot.layouts);
        }
        onEditChange(false);
        onEditable(false);
        setPanelOpen(false);
    };

    const handleSave = async () => {
        setSaving(true);
        // Persist to localStorage immediately so Discard always has a clean baseline
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ widgets: currentWidgets, layouts: currentLayouts }));
        } catch (e) {
            console.error('Failed to persist layout to localStorage:', e);
        }
        try {
            await onSave(currentWidgets, currentLayouts);
        } catch (e) {
            console.error('Failed to save dashboard layout:', e);
        } finally {
            setSaving(false);
        }
        onEditChange(false);
        onEditable(false);
        setPanelOpen(false);
    };

    const handleReset = () => {
        confirmAlert({
            title: 'Reset dashboard layout?',
            message: 'This will restore all widgets to the default layout. Any customisation will be lost.',
            buttons: [
                {
                    label: 'Yes, reset',
                    onClick: () => {
                        localStorage.removeItem(STORAGE_KEY);
                        onWidgetsChange(DEFAULT_LAYOUT.widgets);
                        onLayoutChange(DEFAULT_LAYOUT.layouts);
                    }
                },
                {
                    label: 'Cancel',
                    onClick: () => {}
                }
            ],
            overlayClassName: 'confirm-overlay'
        });
    };

    if (!edit) {
        return (
            <button className={styles.btnManage} onClick={onEnterEdit} disabled={isLoading}>
                <IconManage /> Manage Dashboard
            </button>
        );
    }

    return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* discard — CloseOutlined icon, danger style */}
            <button className={`${styles.btnManage} ${styles.btnDanger}`} onClick={handleCancel}>
                <IconClose /> Discard Changes
            </button>

            {/* add widgets — AppstoreAddOutlined icon + caret */}
            <div style={{ position: 'relative' }} ref={panelRef}>
                    <button className={styles.btnManage} onClick={() => setPanelOpen(v => !v)}>
                        <IconAppstore /> Add Widgets <IconCaret open={panelOpen} />
                    </button>
                    {panelOpen && (
                        <div className={styles.widgetsPanel} ref={panelDropRef}>
                            <div className={styles.widgetsPanelSearch}>
                                <input
                                    autoFocus
                                    placeholder="Search widgets…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                            <div className={styles.widgetsPanelBody}>
                                {categories.map(cat => {
                                    const items = filtered.filter(w => w.category === cat);
                                    if (!items.length) return null;
                                    const isOpen = openCategories.includes(cat);
                                    return (
                                        <div key={cat} className={styles.widgetCollapse}>
                                            {/* category header — click to collapse/expand */}
                                            <button
                                                className={styles.widgetCollapseHeader}
                                                onClick={() => toggleCategory(cat)}
                                            >
                                                <span>{cat}</span>
                                                <IconChevron open={isOpen} />
                                            </button>
                                            {/* items */}
                                            {isOpen && items.map(widget => (
                                                <div
                                                    key={widget.code}
                                                    className={styles.widgetItem}
                                                    draggable
                                                    onClick={() => handleWidgetTap(widget)}
                                                    onDragStart={e => {
                                                        draggingRef.current = true;
                                                        onDragStart(e, widget);
                                                    }}
                                                    onDragEnd={() => {
                                                        draggingRef.current = false;
                                                        setPanelOpen(false);
                                                    }}
                                                >
                                                    <span className={styles.widgetItemIcon}>{renderIcon(widget.type)}</span>
                                                    {widget.name}
                                                    <span className={styles.widgetItemAdd}>＋</span>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <div style={{ padding: '16px', color: '#9ca3af', fontSize: '.85rem', textAlign: 'center' }}>No widgets found</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.divider} />

                {/* reset — no icon */}
                <button className={styles.btnManage} onClick={handleReset} title="Reset to default layout">
                    Reset
                </button>

                {/* save — no icon */}
                <button className={`${styles.btnManage} ${styles.btnPrimary}`} onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Layout'}
                </button>
        </div>
    );
};

export { WIDGET_CATALOGUE, DEFAULT_LAYOUT, STORAGE_KEY };
export default Manage;
